/**
 * Default target wattage and tolerance for the <purmo-calculator> widget, per radiator category.
 *
 * Tweak the numbers in CALCULATOR_DEFAULTS until every category returns at least one result
 * on the first "Calculate" click. Values are clamped to the slider bounds below, so anything
 * outside them is silently pulled back into range.
 *
 * During local development (not in embedded widget mode) the defaults can be overridden from
 * the URL without editing this file:
 *   ?targetWattage=800&toleranceMin=-20&toleranceMax=20
 */

export const WATTAGE_BOUNDS = { min: 50, max: 6000 }
export const TOLERANCE_BOUNDS = { min: -30, max: 30 }

export const DEFAULT_CATEGORY_KEY = 'default'

export const CALCULATOR_DEFAULTS = {
  // Used when no category is known and the calculation method gives no hint.
  default: { targetWattage: 1000, toleranceMin: -10, toleranceMax: 10 },

  // Steel panel radiators: wide range of sizes, so a narrow tolerance still matches.
  panels: { targetWattage: 1000, toleranceMin: -15, toleranceMax: 15 },

  // Decorative / designer radiators: fewer sizes per product, allow a wider band.
  decorative: { targetWattage: 800, toleranceMin: -20, toleranceMax: 20 },

  // Towel rails: low outputs, small ranges.
  towel: { targetWattage: 150, toleranceMin: -30, toleranceMax: 30 },

  // Electric: wattage is the rated capacity as-is, only a handful of discrete values per product.
  electric: { targetWattage: 1000, toleranceMin: -30, toleranceMax: 30 },

  // Convectors and fan-assisted (Ulow) units: higher outputs, several fan speeds.
  convectors: { targetWattage: 1500, toleranceMin: -15, toleranceMax: 15 },
}

/**
 * Initial heating system temperatures (flow / return / room, in °C) for the temperature sliders.
 * Markets not listed in MARKET_SYSTEM_TEMPS use DEFAULT_SYSTEM_TEMPS. Keys are the market ids
 * passed in the `market` attribute (e.g. 'AustriaVN').
 */
export const DEFAULT_SYSTEM_TEMPS = { flow: 55, return: 45, room: 20 }

export const MARKET_SYSTEM_TEMPS = {
  AustriaVN: { flow: 75, return: 65, room: 20 },
}

export const resolveSystemTemps = (market) => ({
  ...DEFAULT_SYSTEM_TEMPS,
  ...(typeof market === 'string' ? MARKET_SYSTEM_TEMPS[market] : null),
})

/**
 * Which view <purmo-calculator> opens with when no `view` attribute or ?view= param is given:
 * 'list' (the results list) or 'matrix'. Markets not listed in MARKET_DEFAULT_VIEW use
 * DEFAULT_VIEW. Example: AustriaVN: 'matrix'.
 */
export const DEFAULT_VIEW = 'list'

export const MARKET_DEFAULT_VIEW = {
  AustriaVN: 'matrix',
}

export const resolveDefaultView = (market) =>
  (typeof market === 'string' && MARKET_DEFAULT_VIEW[market]) || DEFAULT_VIEW

/**
 * When the widget is locked to a product-id the category attribute is ignored, so the category
 * is unknown. The calculation method reported by the /types endpoint is used as a fallback hint.
 */
export const CALCULATION_TO_CATEGORY = {
  'EOP - Electric Radiators': 'electric',
  'THOP - Convector': 'convectors',
  'THOP - Ulow-E': 'convectors',
  'THOP - Ulow E2 - Horizontal for heating and cooling': 'convectors',
  'THOP - Ulow E2 - Vertical for heating and cooling': 'convectors',
}

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const toFiniteNumber = (value) => {
  if (value == null || value === '') return null
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

/**
 * Picks the category key whose defaults apply. Explicit category wins, then the calculation
 * method hint, then the global default.
 */
export const resolveDefaultsKey = ({ category, calculation } = {}) => {
  const normalizedCategory = typeof category === 'string' ? category.trim().toLowerCase() : ''
  if (normalizedCategory && CALCULATOR_DEFAULTS[normalizedCategory]) return normalizedCategory

  const hinted = CALCULATION_TO_CATEGORY[calculation]
  if (hinted && CALCULATOR_DEFAULTS[hinted]) return hinted

  return DEFAULT_CATEGORY_KEY
}

/**
 * Returns { key, targetWattage, toleranceMin, toleranceMax } for the given category and/or
 * calculation method, with an optional override object (e.g. from the URL) applied on top.
 * All values are clamped to the slider bounds and the tolerance band is kept ordered.
 */
export const resolveCalculatorDefaults = ({ category, calculation, override } = {}) => {
  const key = resolveDefaultsKey({ category, calculation })
  const base = { ...CALCULATOR_DEFAULTS[DEFAULT_CATEGORY_KEY], ...CALCULATOR_DEFAULTS[key] }
  const merged = { ...base }

  if (override && typeof override === 'object') {
    for (const field of ['targetWattage', 'toleranceMin', 'toleranceMax']) {
      const numeric = toFiniteNumber(override[field])
      if (numeric != null) merged[field] = numeric
    }
  }

  let toleranceMin = clamp(merged.toleranceMin, TOLERANCE_BOUNDS.min, TOLERANCE_BOUNDS.max)
  let toleranceMax = clamp(merged.toleranceMax, TOLERANCE_BOUNDS.min, TOLERANCE_BOUNDS.max)
  if (toleranceMin > toleranceMax) [toleranceMin, toleranceMax] = [toleranceMax, toleranceMin]

  return {
    key,
    targetWattage: clamp(merged.targetWattage, WATTAGE_BOUNDS.min, WATTAGE_BOUNDS.max),
    toleranceMin,
    toleranceMax,
  }
}

/**
 * Decides the target wattage to show for a loaded product.
 *   1. An explicit URL override of targetWattage always wins (it is a tuning tool).
 *   2. Otherwise the product's own median wattage, when the API supplies one (electric products),
 *      because it is guaranteed to be an existing item's rating.
 *   3. Otherwise the category default.
 */
export const resolveTargetWattage = ({ defaults, productMedianWattage, override } = {}) => {
  const fallback =
    defaults?.targetWattage ?? CALCULATOR_DEFAULTS[DEFAULT_CATEGORY_KEY].targetWattage
  if (toFiniteNumber(override?.targetWattage) != null) return fallback
  const median = toFiniteNumber(productMedianWattage)
  if (median != null && median > 0) return clamp(median, WATTAGE_BOUNDS.min, WATTAGE_BOUNDS.max)
  return fallback
}

/**
 * Reads ?targetWattage=&toleranceMin=&toleranceMax= from a query string. Returns null when none
 * of the three is present, so callers can tell "no override" from "override with defaults".
 */
export const parseDefaultsOverride = (search) => {
  if (typeof search !== 'string' || !search) return null
  const params = new URLSearchParams(search)
  const override = {}
  for (const field of ['targetWattage', 'toleranceMin', 'toleranceMax']) {
    const numeric = toFiniteNumber(params.get(field))
    if (numeric != null) override[field] = numeric
  }
  return Object.keys(override).length ? override : null
}

/**
 * Convenience for components: override from the current page URL, but never in embedded widget
 * mode where the host page's query string is not ours.
 */
export const readDefaultsOverrideFromLocation = () => {
  if (typeof window === 'undefined') return null
  if (window.__WIDGET_MODE__ === true) return null
  return parseDefaultsOverride(window.location.search)
}
